import React from 'react';
import _ from 'lodash';

import { styled } from '@mui/material/styles';



const ContainerDiv = styled('div')({
  flex: '1 0',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch'
});

const HiddenDiv = styled('div')(() => ({
  display: 'none'
}));



const __defaultMaxFileSizeBytes = 10 * 1024 * 1024;



export interface IFileData {
  name: string;
  dataUrl: string;
  file: File;
}



export function fileData2EncodedString(fileData: IFileData | null | undefined) {
  return fileData ? `${fileData.name}/${fileData.dataUrl}` : fileData;
}



export interface IProps {
  acceptTypes?: string[];
  children: (handleClick: () => void) => JSX.Element;
  isDisabled?: boolean;
  onSelectFile: (fileData: IFileData) => void;
  maxFileSizeBytes?: number;
  allowDrop?: boolean;
}

export const FC: React.FC<IProps> = props => {
  const refInput = React.useRef<HTMLInputElement>(null);



  const handleClick = React.useCallback(
    () => {
      if (!props.isDisabled) {
        refInput.current?.click();
      }
    },
    [props.isDisabled]
  );



  const {
    acceptTypes,
    onSelectFile,
    maxFileSizeBytes
  } = props;



  const verifyAndSelectFile = React.useCallback(
    async (file: File | null) => {
      if (
        file &&
        __verifyFileSize(maxFileSizeBytes ?? __defaultMaxFileSizeBytes, file) &&
        __verifyMimeType(acceptTypes, file)
      ) {
        const fileData = await __file2DataUrl(file);
        onSelectFile(fileData);
      }
    },
    [
      maxFileSizeBytes,
      acceptTypes,
      onSelectFile
    ]
  );



  const handleChange = React.useMemo(
    () => {
      const handler: React.ChangeEventHandler<HTMLInputElement> = event => {
        const file = event.target.files?.item(0) ?? null;
        event.target.value = '';
        verifyAndSelectFile(file);
      };

      return handler;
    },
    [verifyAndSelectFile]
  );



  const handleDrop = React.useMemo(
    () => {
      const handler: React.DragEventHandler<HTMLDivElement> = event => {
        event.preventDefault();
        const item = _.first(event.dataTransfer.items);
        if (item?.kind === 'file') {
          const file = item.getAsFile();
          verifyAndSelectFile(file);
        }
      };

      return handler;
    },
    [verifyAndSelectFile]
  );



  return (
    <ContainerDiv
      onDrop={props.allowDrop ? handleDrop : undefined}
      onDragOver={props.allowDrop ?
        (event => event.preventDefault()) :
        undefined}>

      <HiddenDiv>
        <input
          ref={refInput}
          type="file"
          accept={props.acceptTypes ?
            _.join(props.acceptTypes, ', ') :
            undefined}
          onChange={handleChange}
          disabled={props.isDisabled} />
      </HiddenDiv>

      {props.children(handleClick)}

    </ContainerDiv>
  );
};



function __verifyFileSize(maxFileSizeBytes: number, file: File) {
  if (file.size > maxFileSizeBytes) {
    window.alert(__genFileSizeExceededErrorMessage(maxFileSizeBytes));
    return false;
  }
  return true;
}



function __genFileSizeExceededErrorMessage(maxFileSizeBytes: number) {
  let size = "";
  if (maxFileSizeBytes < 1024) {
    size = `${maxFileSizeBytes} B`;
  } else if (maxFileSizeBytes < 1024 * 1024) {
    size = `${Math.ceil(maxFileSizeBytes / 1024)} KiB`;
  } else if (maxFileSizeBytes < 1024 * 1024 * 1024) {
    size = `${Math.ceil(maxFileSizeBytes / (1024 * 1024))} MiB`;
  }

  return `File size too big; cannot exceed ${size}.`;
}



function __verifyMimeType(acceptTypes: string[] | undefined, file: File) {
  if (acceptTypes && !_.includes(acceptTypes, file.type)) {
    window.alert("That file type is not allowed.");
    return false;
  }
  return true;
}



function __file2DataUrl(file: File) {
  return new Promise<IFileData>(resolve => {
    const reader = new FileReader();

    reader.addEventListener('loadend', () => {
      const result = reader.result;
      if (_.isString(result)) {
        resolve({
          name: file.name,
          dataUrl: result,
          file
        });
      }
    }, false);

    reader.readAsDataURL(file);
  });    
}
